/**
 * Travel arche settings — persisted in localStorage.
 */

const SETTINGS_KEY = 'fias-travel-settings';

export type MinuteInterval = 1 | 5;
export type ZoomLevel = 50 | 75 | 100 | 125 | 150;

export const ZOOM_LEVELS: ZoomLevel[] = [50, 75, 100, 125, 150];

export type PinnableToolId = 'dossier' | 'budget' | 'packing' | 'scout' | 'critic';

export interface TravelSettings {
  timeFormat: '12h' | '24h';
  minuteInterval: MinuteInterval;
  zoomEnabled: boolean;
  zoomLevel: ZoomLevel;
  /**
   * When true, opening the Trip Analysis panel automatically runs a fresh
   * analysis if the cached one is older than 1 hour. See
   * TravelAnalysisPanel for the staleness threshold and logic.
   */
  autoRefreshAnalysis: boolean;
  /**
   * Tools pinned to the planner toolbar. Each id also stays visible inside
   * the Tools dropdown so the user can unpin from the same menu.
   */
  pinnedTools: PinnableToolId[];
}

const defaults: TravelSettings = {
  timeFormat: '12h',
  minuteInterval: 5,
  zoomEnabled: true,
  zoomLevel: 100,
  autoRefreshAnalysis: false,
  pinnedTools: [],
};

let cached: TravelSettings | null = null;

export function getTravelSettings(): TravelSettings {
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      cached = { ...defaults, ...JSON.parse(raw) };
      return cached!;
    }
  } catch {
    // ignore
  }
  cached = { ...defaults };
  return cached;
}

export function setTravelSetting<K extends keyof TravelSettings>(
  key: K,
  value: TravelSettings[K],
): void {
  const settings = getTravelSettings();
  settings[key] = value;
  cached = settings;
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

/** Toggle a tool's pinned state, returning the updated list. */
export function togglePinnedTool(id: PinnableToolId): PinnableToolId[] {
  const current = getTravelSettings().pinnedTools;
  const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
  setTravelSetting('pinnedTools', next);
  return next;
}

/** Format a 24h time string (HH:MM) for display based on user preference. */
export function formatTime(time: string | null): string {
  if (!time) return '';
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return time;
  const h = parseInt(match[1], 10);
  const m = match[2];
  const { timeFormat } = getTravelSettings();
  if (timeFormat === '24h') {
    return `${String(h).padStart(2, '0')}:${m}`;
  }
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${period}`;
}
